/*
 * King Living useToast
 * Standardized toast for account and form feedback: top-center position, success/error styling per Figma.
 */

import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  CloseButton,
  useToast as useChakraToast,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const DEFAULT_POSITION = 'top';
const DEFAULT_DURATION = 5000;

/**
 * useToast - Standardized toast for success/error messages (e.g. account forms).
 * Displays at top middle; success/error use Alert theme (green/red per Figma).
 *
 * @returns {Function} showToast({ title, status, ... })
 */
export function useToast() {
  const toast = useChakraToast();

  return (options = {}) => {
    const {
      title,
      status = 'info',
      action,
      position = DEFAULT_POSITION,
      duration = DEFAULT_DURATION,
      variant = 'subtle',
      isClosable = true,
      containerStyle: containerStyleOverride,
      ...rest
    } = options;

    const toastConfig = {
      title,
      status,
      isClosable,
      position,
      duration,
      variant,
      containerStyle: {
        maxWidth: '480px',
        width: 'calc(100% - 32px)',
        mx: 'auto',
        ...containerStyleOverride,
      },
      render: ({onClose}) => (
        <Alert
          borderRadius="base"
          py={3}
          px={4}
          status={status}
          variant={variant}
          width="100%"
          alignItems="flex-start"
          gap={2}
        >
          <AlertIcon flexShrink={0} />
          <AlertTitle flex="1" minWidth={0}>
            {title}
          </AlertTitle>
          {action}
          {isClosable && (
            <CloseButton flexShrink={0} marginInlineStart="auto" onClick={onClose} />
          )}
        </Alert>
      ),
      ...rest,
    };

    toast(toastConfig);
  };
}
